import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Download,
  Edit2,
  Trash2,
  Package,
  MoreVertical,
  Barcode,
} from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import { cn, downloadCSV } from '../../lib/utils';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/Dialog';
import Checkbox from '../../components/ui/Checkbox';
import Label from '../../components/ui/Label';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  type Product,
  type CreateProductData,
  type UpdateProductData,
} from '../../hooks/useProducts';

interface ProductFormData {
  code: string;
  description: string;
  arDescription: string;
  unitCode: string;
  categoryCode: string;
  basePrice: number;
  lastPurchasePrice: number;
  barcodes: string;
  minQty: number;
  maxQty: number;
  criticalQty: number;
  isRecipe: boolean;
  isActive: boolean;
}

const initialFormData: ProductFormData = {
  code: '',
  description: '',
  arDescription: '',
  unitCode: 'PCS',
  categoryCode: '',
  basePrice: 0,
  lastPurchasePrice: 0,
  barcodes: '',
  minQty: 0,
  maxQty: 0,
  criticalQty: 0,
  isRecipe: false,
  isActive: true,
};

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { toast } = useToast();

  // State
  const [page, setPage] = useState(1);
  const [searchInputValue, setSearchInputValue] = useState(''); // Immediate input display
  const [searchTerm, setSearchTerm] = useState(''); // Debounced value for API
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const isRtl = i18n.dir() === 'rtl';

  // Fetch products
  const {
    data: productsData,
    isLoading,
    error,
  } = useProducts({
    page,
    limit: 20,
    search: searchTerm || undefined,
  });

  const products = productsData?.data ?? [];
  const pagination = productsData?.pagination;

  // Mutations
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  // Form validation
  const validateForm = useCallback(
    (data: ProductFormData, isEdit: boolean): Record<string, string> => {
      const errors: Record<string, string> = {};

      if (!isEdit && !data.code.trim()) {
        errors.code = t('products.validation.codeRequired', 'Product code is required');
      }

      if (!data.description.trim()) {
        errors.description = t('products.validation.descriptionRequired', 'Description is required');
      }

      if (!data.unitCode.trim()) {
        errors.unitCode = t('products.validation.unitRequired', 'Unit is required');
      }

      if (data.basePrice < 0) {
        errors.basePrice = t('products.validation.pricePositive', 'Price must be positive');
      }

      return errors;
    },
    [t]
  );

  // Handlers
  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedProduct(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedProduct(null);
  };

  const handleOpenEditDialog = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      code: product.code,
      description: product.description,
      arDescription: product.arDescription || '',
      unitCode: product.unitCode,
      categoryCode: product.categoryCode || '',
      basePrice: product.pricing?.basePrice || 0,
      lastPurchasePrice: product.pricing?.lastPurchasePrice || 0,
      barcodes: product.barcodes?.join(', ') || '',
      minQty: product.inventory?.minQty || 0,
      maxQty: product.inventory?.maxQty || 0,
      criticalQty: product.inventory?.criticalQty || 0,
      isRecipe: product.isRecipe || false,
      isActive: product.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateProduct = async () => {
    const errors = validateForm(formData, false);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const barcodesList = formData.barcodes
      .split(',')
      .map((b) => b.trim())
      .filter((b) => b.length > 0);

    const createData: CreateProductData = {
      code: formData.code,
      description: formData.description,
      arDescription: formData.arDescription || undefined,
      unitCode: formData.unitCode,
      categoryCode: formData.categoryCode || undefined,
      pricing: {
        basePrice: formData.basePrice,
        lastPurchasePrice: formData.lastPurchasePrice || undefined,
      },
      barcodes: barcodesList.length > 0 ? barcodesList : undefined,
      inventory: {
        minQty: formData.minQty,
        maxQty: formData.maxQty,
        criticalQty: formData.criticalQty,
      },
      isRecipe: formData.isRecipe,
    };

    try {
      await createProductMutation.mutateAsync(createData);
      toast.success(t('products.messages.createSuccess', 'Product created successfully'));
      handleCloseCreateDialog();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred');
      toast.error(t('common.error', 'Error'), message);
    }
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;

    const errors = validateForm(formData, true);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const barcodesList = formData.barcodes
      .split(',')
      .map((b) => b.trim())
      .filter((b) => b.length > 0);

    const updateData: UpdateProductData = {
      description: formData.description,
      arDescription: formData.arDescription || undefined,
      unitCode: formData.unitCode,
      categoryCode: formData.categoryCode || undefined,
      pricing: {
        basePrice: formData.basePrice,
        lastPurchasePrice: formData.lastPurchasePrice || undefined,
      },
      barcodes: barcodesList,
      inventory: {
        minQty: formData.minQty,
        maxQty: formData.maxQty,
        criticalQty: formData.criticalQty,
      },
      isRecipe: formData.isRecipe,
      isActive: formData.isActive,
    };

    try {
      await updateProductMutation.mutateAsync({
        id: selectedProduct.id,
        data: updateData,
      });
      toast.success(t('products.messages.updateSuccess', 'Product updated successfully'));
      handleCloseEditDialog();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred');
      toast.error(t('common.error', 'Error'), message);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      await deleteProductMutation.mutateAsync(selectedProduct.id);
      toast.success(t('products.messages.deleteSuccess', 'Product deleted successfully'));
      handleCloseDeleteDialog();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred');
      toast.error(t('common.error', 'Error'), message);
    }
  };

  const handleExportCSV = useCallback(() => {
    const exportData = products.map((product) => ({
      [t('products.fields.code', 'Code')]: product.code,
      [t('products.fields.description', 'Description')]: product.description,
      [t('products.fields.arDescription', 'Arabic Description')]: product.arDescription || '',
      [t('products.fields.unit', 'Unit')]: product.unitCode,
      [t('products.fields.price', 'Price')]: product.pricing?.basePrice || 0,
      [t('products.fields.barcodes', 'Barcodes')]: product.barcodes?.join(', ') || '',
      [t('products.fields.status', 'Status')]: product.isActive
        ? t('common.active', 'Active')
        : t('common.inactive', 'Inactive'),
    }));

    downloadCSV(exportData, `products_${new Date().toISOString().split('T')[0]}`);
  }, [products, t]);

  // Debounced search using useRef for cleanup
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    // Update input immediately for responsive feedback
    setSearchInputValue(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Debounce the API search term
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setPage(1);
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[400px]"
        role="status"
        aria-live="polite"
        aria-label={t('common.loading', 'Loading products...')}
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error) {
    const errorMessage = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred');
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[400px] text-center"
        role="alert"
        aria-live="assertive"
      >
        <Package className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('common.error', 'Error')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('products.title', 'Products')}
        subtitle={t('products.subtitle', 'Manage your product catalog')}
        actions={
          hasPermission('items:write') && (
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('products.addProduct', 'Add Product')}
            </Button>
          )
        }
      />

      <Card>
        <CardContent className="p-0">
          {/* Search and Actions Bar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder={t('products.searchPlaceholder', 'Search products...')}
                  value={searchInputValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
                  aria-label={t('products.searchPlaceholder', 'Search products...')}
                />
              </div>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                leftIcon={<Download className="h-4 w-4" />}
              >
                {t('common.exportCSV', 'Export CSV')}
              </Button>
            </div>
          </div>

          {/* Products Table */}
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('products.noProducts', 'No products found')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                {t('products.noProductsDescription', 'Get started by adding your first product.')}
              </p>
              {hasPermission('items:write') && (
                <Button
                  className="mt-4"
                  onClick={() => setIsCreateDialogOpen(true)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  {t('products.addProduct', 'Add Product')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('products.fields.code', 'Code')}</TableHead>
                    <TableHead>{t('products.fields.name', 'Name')}</TableHead>
                    <TableHead>{t('products.fields.unit', 'Unit')}</TableHead>
                    <TableHead>{t('products.fields.price', 'Price')}</TableHead>
                    <TableHead>{t('products.fields.status', 'Status')}</TableHead>
                    <TableHead className="ltr:text-right rtl:text-left">
                      {t('common.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium font-mono">{product.code}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{product.name}</div>
                          {product.nameEn && product.nameEn !== product.name && (
                            <div className="text-sm text-gray-500">{product.nameEn}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{product.unitCode}</TableCell>
                      <TableCell>
                        {product.pricing?.basePrice?.toLocaleString(
                          i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                          { minimumFractionDigits: 2 }
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            product.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          )}
                        >
                          {product.isActive
                            ? t('common.active', 'Active')
                            : t('common.inactive', 'Inactive')}
                        </span>
                      </TableCell>
                      <TableCell className="ltr:text-right rtl:text-left">
                        <Menu as="div" className="relative inline-block text-left">
                          <MenuButton
                            as={Button}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <span className="sr-only">{t('common.actions', 'Actions')}</span>
                            <MoreVertical className="h-4 w-4" />
                          </MenuButton>
                          <MenuItems
                            className={cn(
                              'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                              isRtl ? 'left-0' : 'right-0'
                            )}
                          >
                            <div className="py-1">
                              {hasPermission('items:write') && (
                                <MenuItem>
                                  {({ focus }) => (
                                    <button
                                      onClick={() => handleOpenEditDialog(product)}
                                      className={cn(
                                        'w-full flex items-center gap-2 px-4 py-2 text-sm',
                                        focus
                                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                          : 'text-gray-700 dark:text-gray-300'
                                      )}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                      {t('products.editProduct', 'Edit Product')}
                                    </button>
                                  )}
                                </MenuItem>
                              )}
                              {hasPermission('items:delete') && (
                                <MenuItem>
                                  {({ focus }) => (
                                    <button
                                      onClick={() => handleOpenDeleteDialog(product)}
                                      className={cn(
                                        'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
                                        focus && 'bg-red-50 dark:bg-red-900/20'
                                      )}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      {t('products.deleteProduct', 'Delete Product')}
                                    </button>
                                  )}
                                </MenuItem>
                              )}
                            </div>
                          </MenuItems>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <nav
                  className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
                  aria-label={t('pagination.navigation', 'Pagination navigation')}
                >
                  <div className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
                    {t('common.showing', 'Showing')} {(page - 1) * 20 + 1}-
                    {Math.min(page * 20, pagination.total)} {t('common.of', 'of')}{' '}
                    {pagination.total}
                  </div>
                  <div className="flex gap-2" role="group" aria-label={t('pagination.controls', 'Pagination controls')}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      aria-label={t('pagination.goToPrevious', 'Go to previous page')}
                    >
                      {t('common.previous', 'Previous')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page === pagination.totalPages}
                      aria-label={t('pagination.goToNext', 'Go to next page')}
                    >
                      {t('common.next', 'Next')}
                    </Button>
                  </div>
                </nav>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogHeader onClose={handleCloseCreateDialog}>
          <DialogTitle>{t('products.addProduct', 'Add Product')}</DialogTitle>
          <DialogDescription>
            {t('products.addProductDescription', 'Add a new product to your catalog')}
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <Input
              label={t('products.fields.code', 'Code')}
              name="code"
              value={formData.code}
              onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
              error={formErrors.code}
              isRequired
            />
            <Input
              label={t('products.fields.description', 'Description (English)')}
              name="description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              error={formErrors.description}
              isRequired
            />
            <Input
              label={t('products.fields.arDescription', 'Description (Arabic)')}
              name="arDescription"
              value={formData.arDescription}
              onChange={(e) => setFormData((prev) => ({ ...prev, arDescription: e.target.value }))}
              dir="rtl"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('products.fields.unit', 'Unit')}
                name="unitCode"
                value={formData.unitCode}
                onChange={(e) => setFormData((prev) => ({ ...prev, unitCode: e.target.value }))}
                error={formErrors.unitCode}
                placeholder="PCS, KG, LTR"
                isRequired
              />
              <Input
                label={t('products.fields.category', 'Category')}
                name="categoryCode"
                value={formData.categoryCode}
                onChange={(e) => setFormData((prev) => ({ ...prev, categoryCode: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('products.fields.price', 'Selling Price')}
                name="basePrice"
                type="number"
                min={0}
                step={0.01}
                value={formData.basePrice}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))
                }
                error={formErrors.basePrice}
              />
              <Input
                label={t('products.fields.purchasePrice', 'Purchase Price')}
                name="lastPurchasePrice"
                type="number"
                min={0}
                step={0.01}
                value={formData.lastPurchasePrice}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    lastPurchasePrice: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <Input
              label={t('products.fields.barcodes', 'Barcodes')}
              name="barcodes"
              value={formData.barcodes}
              onChange={(e) => setFormData((prev) => ({ ...prev, barcodes: e.target.value }))}
              placeholder={t('products.barcodesPlaceholder', 'Comma-separated barcodes')}
              leftIcon={<Barcode className="h-4 w-4" />}
            />

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <Label className="mb-3 block">
                {t('products.inventorySettings', 'Inventory Settings')}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label={t('products.fields.minQty', 'Min Qty')}
                  name="minQty"
                  type="number"
                  min={0}
                  value={formData.minQty}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, minQty: parseInt(e.target.value) || 0 }))
                  }
                />
                <Input
                  label={t('products.fields.maxQty', 'Max Qty')}
                  name="maxQty"
                  type="number"
                  min={0}
                  value={formData.maxQty}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, maxQty: parseInt(e.target.value) || 0 }))
                  }
                />
                <Input
                  label={t('products.fields.criticalQty', 'Critical')}
                  name="criticalQty"
                  type="number"
                  min={0}
                  value={formData.criticalQty}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      criticalQty: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <Checkbox
              id="isRecipe"
              label={t('products.fields.isRecipe', 'This is a recipe/composite item')}
              checked={formData.isRecipe}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isRecipe: checked }))}
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseCreateDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleCreateProduct} isLoading={createProductMutation.isPending}>
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <DialogHeader onClose={handleCloseEditDialog}>
          <DialogTitle>{t('products.editProduct', 'Edit Product')}</DialogTitle>
          <DialogDescription>{selectedProduct?.code}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <Input
              label={t('products.fields.code', 'Code')}
              name="code"
              value={formData.code}
              disabled
            />
            <Input
              label={t('products.fields.description', 'Description (English)')}
              name="description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              error={formErrors.description}
              isRequired
            />
            <Input
              label={t('products.fields.arDescription', 'Description (Arabic)')}
              name="arDescription"
              value={formData.arDescription}
              onChange={(e) => setFormData((prev) => ({ ...prev, arDescription: e.target.value }))}
              dir="rtl"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('products.fields.unit', 'Unit')}
                name="unitCode"
                value={formData.unitCode}
                onChange={(e) => setFormData((prev) => ({ ...prev, unitCode: e.target.value }))}
                error={formErrors.unitCode}
                isRequired
              />
              <Input
                label={t('products.fields.category', 'Category')}
                name="categoryCode"
                value={formData.categoryCode}
                onChange={(e) => setFormData((prev) => ({ ...prev, categoryCode: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('products.fields.price', 'Selling Price')}
                name="basePrice"
                type="number"
                min={0}
                step={0.01}
                value={formData.basePrice}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))
                }
              />
              <Input
                label={t('products.fields.purchasePrice', 'Purchase Price')}
                name="lastPurchasePrice"
                type="number"
                min={0}
                step={0.01}
                value={formData.lastPurchasePrice}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    lastPurchasePrice: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <Input
              label={t('products.fields.barcodes', 'Barcodes')}
              name="barcodes"
              value={formData.barcodes}
              onChange={(e) => setFormData((prev) => ({ ...prev, barcodes: e.target.value }))}
              placeholder={t('products.barcodesPlaceholder', 'Comma-separated barcodes')}
              leftIcon={<Barcode className="h-4 w-4" />}
            />

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <Label className="mb-3 block">
                {t('products.inventorySettings', 'Inventory Settings')}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label={t('products.fields.minQty', 'Min Qty')}
                  name="minQty"
                  type="number"
                  min={0}
                  value={formData.minQty}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, minQty: parseInt(e.target.value) || 0 }))
                  }
                />
                <Input
                  label={t('products.fields.maxQty', 'Max Qty')}
                  name="maxQty"
                  type="number"
                  min={0}
                  value={formData.maxQty}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, maxQty: parseInt(e.target.value) || 0 }))
                  }
                />
                <Input
                  label={t('products.fields.criticalQty', 'Critical')}
                  name="criticalQty"
                  type="number"
                  min={0}
                  value={formData.criticalQty}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      criticalQty: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <Checkbox
              id="edit-isRecipe"
              label={t('products.fields.isRecipe', 'This is a recipe/composite item')}
              checked={formData.isRecipe}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isRecipe: checked }))}
            />

            <Checkbox
              id="edit-isActive"
              label={t('products.fields.isActive', 'Product is active')}
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseEditDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleUpdateProduct} isLoading={updateProductMutation.isPending}>
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogHeader onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t('products.deleteProduct', 'Delete Product')}</DialogTitle>
          <DialogDescription>
            {t('products.confirmDelete', 'Are you sure you want to delete this product?')}
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t(
              'products.confirmDeleteDescription',
              'This action will deactivate the product. It can be restored later.'
            )}
          </p>
          {selectedProduct && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {selectedProduct.code}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedProduct.name}</p>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDeleteDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteProduct}
            isLoading={deleteProductMutation.isPending}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
